/**
 * WebSocketProxy - A WebSocket-like class that delegates actual websocket
 * operations to a Web Worker for off-main-thread processing.
 *
 * This proxy implements the WebSocket interface so it can be used as a
 * drop-in replacement for native WebSocket or SockJS connections.
 */

export interface WebSocketProxyConfig {
  worker: Worker;
  onLog?: (message: string) => void;
  onError?: () => void;
  onOpen?: () => void;
}

// Module-level state for the proxy system
let currentProxy: WebSocketProxy | null = null;
let workerInstance: Worker | null = null;
let logHandler: ((message: string) => void) | null = null;
let errorHandler: (() => void) | null = null;
let openHandler: (() => void) | null = null;
let isWorkerReady = false;
let pendingConnect = false;

/**
 * Initialize the WebSocket proxy system with a worker instance
 */
export function initializeProxySystem(config: WebSocketProxyConfig): void {
  workerInstance = config.worker;
  logHandler = config.onLog || console.log;
  errorHandler = config.onError || null;
  openHandler = config.onOpen || null;
  isWorkerReady = false;
  pendingConnect = false;
  setupWorkerMessageHandler(config.worker);
}

/**
 * Get the current active proxy instance
 */
export function getCurrentProxy(): WebSocketProxy | null {
  return currentProxy;
}

/**
 * Clean up the current proxy and reset state
 */
export function cleanupCurrentProxy(): void {
  if (currentProxy) {
    try {
      // Try to close the connection gracefully
      if (currentProxy.readyState === WebSocketProxy.CONNECTING || 
          currentProxy.readyState === WebSocketProxy.OPEN) {
        currentProxy.close(1000, 'Cleanup');
      }
    } catch (e) {
      // Ignore errors during cleanup
    }
    currentProxy = null;
  }
}

/**
 * Notify the worker to disconnect and clean up
 */
export function disconnectWorker(): void {
  if (workerInstance) {
    try {
      workerInstance.postMessage({ type: 'disconnect' });
    } catch (e) {
      // Ignore errors if worker is not available
    }
  }
}

/**
 * Central message handler for the worker - routes messages to the current proxy
 */
function setupWorkerMessageHandler(worker: Worker): void {
  console.log('[WebSocketProxy] Setting up worker message handler');
  
  worker.onmessage = (event: MessageEvent) => {
    const { type, payload } = event.data;

    // Handle initialization response
    if (type === 'initialized') {
      if (logHandler) logHandler('WebSocket worker initialized');
      return;
    }

    // Handle log messages
    if (type === 'log') {
      if (logHandler) logHandler(payload);
      return;
    }

    // Handle ready message - worker has finished loading
    if (type === 'ready') {
      console.log('[WebSocketProxy] Worker is ready');
      isWorkerReady = true;
      
      // If there's a pending connect, send it now
      if (pendingConnect && workerInstance) {
        console.log('[WebSocketProxy] Sending pending connect message');
        workerInstance.postMessage({ type: 'connect' });
        pendingConnect = false;
      }
      return;
    }

    // Handle open messages - notify success handler to reset failure count
    if (type === 'open') {
      if (logHandler) {
        logHandler('WebSocket connection opened');
      }
      if (openHandler) {
        openHandler();
      }
    }

    // Handle error messages - notify the error handler for fallback
    if (type === 'error') {
      if (logHandler) {
        logHandler(`WebSocket error: ${payload}`);
      }
      if (errorHandler) {
        errorHandler();
      }
    }

    // Route WebSocket events to the current proxy
    if (currentProxy) {
      currentProxy.handleWorkerMessage(type, payload);
    }
  };
}

export class WebSocketProxy implements WebSocket {
  // WebSocket ready state constants
  static readonly CONNECTING = 0;
  static readonly OPEN = 1;
  static readonly CLOSING = 2;
  static readonly CLOSED = 3;

  readonly CONNECTING = WebSocketProxy.CONNECTING;
  readonly OPEN = WebSocketProxy.OPEN;
  readonly CLOSING = WebSocketProxy.CLOSING;
  readonly CLOSED = WebSocketProxy.CLOSED;

  // Private state
  private _readyState: number = WebSocketProxy.CONNECTING;
  private _bufferedAmount: number = 0;
  private _extensions: string = '';
  private _protocol: string = '';
  private _url: string = '';
  private _binaryType: BinaryType = 'blob';
  private _eventListeners: Map<string, Set<EventListenerOrEventListenerObject>> = new Map();

  // WebSocket event handlers
  onopen: ((this: WebSocket, ev: Event) => any) | null = null;
  onmessage: ((this: WebSocket, ev: MessageEvent) => any) | null = null;
  onclose: ((this: WebSocket, ev: CloseEvent) => any) | null = null;
  onerror: ((this: WebSocket, ev: Event) => any) | null = null;

  // Getters for readonly properties
  get readyState(): number {
    return this._readyState;
  }

  get bufferedAmount(): number {
    return this._bufferedAmount;
  }

  get extensions(): string {
    return this._extensions;
  }

  get protocol(): string {
    return this._protocol;
  }

  get url(): string {
    return this._url;
  }

  get binaryType(): BinaryType {
    return this._binaryType;
  }

  set binaryType(value: BinaryType) {
    this._binaryType = value;
  }

  constructor() {
    // Clean up any existing proxy before registering this one
    if (currentProxy && currentProxy !== this) {
      cleanupCurrentProxy();
    }
    // Register this proxy as the current active one
    currentProxy = this;
  }

  /**
   * Dispose of this proxy and clean up resources
   */
  dispose(): void {
    // Clear event listeners
    this._eventListeners.clear();
    
    // Clear event handlers
    this.onopen = null;
    this.onmessage = null;
    this.onclose = null;
    this.onerror = null;
    
    // Deregister as current proxy if this is the active one
    if (currentProxy === this) {
      currentProxy = null;
    }
  }

  /**
   * Handle messages from the worker - called by the central message handler
   */
  handleWorkerMessage(type: string, payload: any): void {
    switch (type) {
      case 'open':
        this._readyState = WebSocketProxy.OPEN;
        this._dispatchEvent(new Event('open'));
        if (this.onopen) this.onopen.call(this, new Event('open'));
        break;

      case 'message':
        const messageEvent = new MessageEvent('message', { data: payload });
        this._dispatchEvent(messageEvent);
        if (this.onmessage) this.onmessage.call(this, messageEvent);
        break;

      case 'close':
        this._readyState = WebSocketProxy.CLOSED;
        const closeEvent = new CloseEvent('close', {
          code: payload?.code || 1000,
          reason: payload?.reason || '',
          wasClean: payload?.wasClean ?? true
        });
        this._dispatchEvent(closeEvent);
        if (this.onclose) this.onclose.call(this, closeEvent);
        break;

      case 'error':
        const errorEvent = new Event('error');
        this._dispatchEvent(errorEvent);
        if (this.onerror) this.onerror.call(this, errorEvent);
        break;
    }
  }

  /**
   * Initiate connection via the worker
   */
  connect(): void {
    this._readyState = WebSocketProxy.CONNECTING;
    
    if (!workerInstance) {
      throw new Error('WebSocket proxy system not initialized.');
    }
    
    if (isWorkerReady) {
      console.log('[WebSocketProxy] Worker is ready, sending connect message...');
      workerInstance.postMessage({ type: 'connect' });
      console.log('[WebSocketProxy] Connect message sent to worker');
    } else {
      console.log('[WebSocketProxy] Worker not ready yet, queuing connect message');
      pendingConnect = true;
    }
  }

  /**
   * Send data through the WebSocket via the worker
   */
  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
    if (this._readyState !== WebSocketProxy.OPEN) {
      throw new DOMException('WebSocket is not open', 'InvalidStateError');
    }
    if (!workerInstance) {
      throw new Error('WebSocket proxy system not initialized.');
    }
    workerInstance.postMessage({ type: 'send', payload: data });
  }

  /**
   * Close the WebSocket connection
   */
  close(code?: number, reason?: string): void {
    if (this._readyState === WebSocketProxy.CLOSING || this._readyState === WebSocketProxy.CLOSED) {
      return;
    }
    this._readyState = WebSocketProxy.CLOSING;
    if (!workerInstance) {
      throw new Error('WebSocket proxy system not initialized.');
    }
    workerInstance.postMessage({ type: 'stopHeartbeat' });
    workerInstance.postMessage({ type: 'close', payload: { code, reason } });
  }

  /**
   * Manually start heartbeat (useful after STOMP CONNECTED frame)
   */
  startHeartbeat(): void {
    if (!workerInstance) {
      throw new Error('WebSocket proxy system not initialized.');
    }
    workerInstance.postMessage({ type: 'startHeartbeat' });
  }

  /**
   * Manually stop heartbeat
   */
  stopHeartbeat(): void {
    if (!workerInstance) {
      throw new Error('WebSocket proxy system not initialized.');
    }
    workerInstance.postMessage({ type: 'stopHeartbeat' });
  }

  /**
   * Add an event listener
   */
  addEventListener<K extends keyof WebSocketEventMap>(
    type: K,
    listener: (this: WebSocket, ev: WebSocketEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions
  ): void;
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ): void {
    if (!this._eventListeners.has(type)) {
      this._eventListeners.set(type, new Set());
    }
    this._eventListeners.get(type)!.add(listener);
  }

  /**
   * Remove an event listener
   */
  removeEventListener<K extends keyof WebSocketEventMap>(
    type: K,
    listener: (this: WebSocket, ev: WebSocketEventMap[K]) => any,
    options?: boolean | EventListenerOptions
  ): void;
  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions
  ): void {
    if (this._eventListeners.has(type)) {
      this._eventListeners.get(type)!.delete(listener);
    }
  }

  /**
   * Dispatch an event
   */
  dispatchEvent(event: Event): boolean {
    return this._dispatchEvent(event);
  }

  /**
   * Internal event dispatch implementation
   */
  private _dispatchEvent(event: Event): boolean {
    const listeners = this._eventListeners.get(event.type);
    if (listeners) {
      listeners.forEach(listener => {
        if (typeof listener === 'function') {
          listener.call(this, event);
        } else {
          listener.handleEvent(event);
        }
      });
    }
    return !event.defaultPrevented;
  }
}
